import { apiClient } from '@api'

/**
 * Workflow Service
 *
 * Service layer for managing workflows.
 * Workflows define multi-step approval processes for checklist items.
 */
class WorkflowService {
	/**
	 * Get all workflows for a company
	 * @param {Object} params - Query parameters (company_id, status, search, sort_by, sort_order)
	 * @returns {Promise<Object>} Workflows data with stats
	 */
	async getAll(params = {}) {
		const queryParams = new URLSearchParams()

		if (params.company_id) queryParams.append('company_id', params.company_id)
		if (params.status) queryParams.append('status', params.status)
		if (params.search) queryParams.append('search', params.search)
		if (params.sort_by) queryParams.append('sort_by', params.sort_by)
		if (params.sort_order) queryParams.append('sort_order', params.sort_order)

		const queryString = queryParams.toString()
		const endpoint = queryString ? `/workflows?${queryString}` : '/workflows'

		return await apiClient.get(endpoint)
	}

	/**
	 * Get workflow by ID with steps
	 * @param {string|number} id - Workflow ID
	 * @returns {Promise<Object>} Workflow data with steps
	 */
	async getById(id) {
		return await apiClient.get(`/workflows/${id}`)
	}

	/**
	 * Create new workflow with steps
	 * @param {Object} workflowData - Workflow data including steps array
	 * @returns {Promise<Object>} Created workflow data
	 */
	async create(workflowData) {
		return await apiClient.post('/workflows', workflowData)
	}

	/**
	 * Update workflow
	 * @param {string|number} id - Workflow ID
	 * @param {Object} workflowData - Updated workflow data including steps
	 * @returns {Promise<Object>} Updated workflow data
	 */
	async update(id, workflowData) {
		return await apiClient.put(`/workflows/${id}`, workflowData)
	}

	/**
	 * Delete workflow (archives if has attached projects)
	 * @param {string|number} id - Workflow ID
	 * @returns {Promise<Object>} Success response
	 */
	async delete(id) {
		return await apiClient.delete(`/workflows/${id}`)
	}

	/**
	 * Toggle workflow status (active/disabled)
	 * @param {string|number} id - Workflow ID
	 * @returns {Promise<Object>} Updated workflow data
	 */
	async toggle(id) {
		return await apiClient.post(`/workflows/${id}/toggle`, {})
	}

	/**
	 * Get active workflows for dropdown (uses auth company_id)
	 * @returns {Promise<Object>} Active workflows list { success, data }
	 */
	async getActive() {
		return await apiClient.get('/workflows/active')
	}

	/**
	 * Get workflow statistics
	 * @param {string|number} companyId - Company ID
	 * @returns {Promise<Object>} Workflow stats
	 */
	async getStats(companyId) {
		return await apiClient.get(`/workflows/stats?company_id=${companyId}`)
	}

	/**
	 * Get users for approver selection (by workflow)
	 * @param {string|number} workflowId - Workflow ID
	 * @param {string} role - Optional role filter
	 * @returns {Promise<Object>} Users list
	 */
	async getWorkflowUsers(workflowId, role = null) {
		const params = role ? `?role=${role}` : ''
		return await apiClient.get(`/workflows/${workflowId}/users${params}`)
	}

	/**
	 * Get users by company for approver selection (for create form)
	 * @param {string|number} companyId - Company ID
	 * @param {string} role - Optional role filter
	 * @returns {Promise<Object>} Users list
	 */
	async getCompanyUsers(companyId, role = null) {
		const params = role ? `?role=${role}` : ''
		return await apiClient.get(`/workflows/company/${companyId}/users${params}`)
	}
}

// Export singleton instance
export const workflowService = new WorkflowService()
export default workflowService
